import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authAPI } from '../../services/api';
import './Auth.css';

/**
 * Shown after a user registers with email/password.
 * Expected navigation state: { email }
 */
function VerifyEmailSent() {
  const location = useLocation();

  // Email can come from navigate('/verify-email-sent', { state: { email } })
  const initialEmail = useMemo(() => {
    const stateEmail = location.state?.email;
    return typeof stateEmail === 'string' ? stateEmail : '';
  }, [location.state]);

  const [email, setEmail] = useState(initialEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const handleResend = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    const normalized = (email || '').trim().toLowerCase();
    if (!normalized) {
      setError('Please enter your email address');
      return;
    }

    try {
      setIsSubmitting(true);

      // Prefer API wrapper if present.
      if (typeof authAPI.resendVerification === 'function') {
        const res = await authAPI.resendVerification(normalized);
        setMessage(res.data?.message || 'Verification email sent.');
        return;
      }

      // Fallback direct fetch (in case authAPI wasn’t updated yet)
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/resend-verification`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: normalized }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to resend verification email');
      setMessage(data.message || 'Verification email sent.');
    } catch (err) {
      console.error('Resend verification error:', err);
      setError(err.message || 'Failed to resend verification email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Verify Your Email</h1>
        <h2>Check Your Inbox</h2>

        <p style={{ color: '#666', marginTop: 8 }}>
          We sent a verification link to the email address you used to create your account.
        </p>

        {initialEmail ? (
          <div
            style={{
              marginTop: 16,
              padding: '10px 12px',
              border: '1px solid #ddd',
              borderRadius: 8,
              background: '#fafafa',
              wordBreak: 'break-word',
            }}
          >
            <strong>{initialEmail}</strong>
          </div>
        ) : null}

        <div style={{ marginTop: 18 }}>
          <p style={{ color: '#666', marginBottom: 8 }}>
            Didn&apos;t get the email? You can resend it below.
          </p>

          {error && <div className="error-message">{error}</div>}
          {message && (
            <div className="success-message" style={{ marginBottom: 12 }}>
              {message}
            </div>
          )}

          <form onSubmit={handleResend}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                disabled={isSubmitting}
              />
            </div>

            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? 'Sending…' : 'Resend Verification Email'}
            </button>
          </form>
        </div>

        <p className="auth-link" style={{ marginTop: 20 }}>
          <Link to="/login">Back to Login</Link>
        </p>
      </div>
    </div>
  );
}

export default VerifyEmailSent;
